/* ============================================================================
   ORW More-in-Collection -- Mobile infinite scroll
   ============================================================================
   On mobile viewports (<= 749px), the More-in-Collection block on the PDP
   keeps loading additional products from the SAME collection as the user
   scrolls, instead of stopping at the Liquid-rendered cap. Desktop and
   tablet are untouched.

   - Reads collection URL + current product URL from data-* on the grid.
   - On mobile only, places a sentinel inside the grid (spans both cols)
     and observes intersection with IntersectionObserver + 400px rootMargin.
   - When the sentinel scrolls into view, fetch `<collection-url>?page=N`,
     parse out `.orw-product-card` elements from the response, skip the
     current product and any card already shown (deduped by href), and
     append the rest into our grid before the sentinel.
   - Stops once the fetched page has no `.orw-product-list__page-btn--next`.
   - Reload on resize-across-threshold so the layout matches the new mode.
   ============================================================================ */
(function () {
  'use strict';

  var MOBILE_MAX = 749;
  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  var grid = document.querySelector('.orw-more-in-collection__grid[data-orw-more-grid]');
  if (!grid) return;

  var collectionUrl = grid.getAttribute('data-orw-collection-url');
  var currentProductUrl = grid.getAttribute('data-orw-current-product-url');
  if (!collectionUrl) return;

  /* Skip all setup on non-mobile viewports. The resize handler below
     reloads the page on threshold crossings, so the next mobile load
     will re-run this script and set up the sentinel cleanly. Skipping
     here keeps the grid visually identical on desktop / tablet. */
  if (!isMobile()) {
    var lastIsMobileEarly = isMobile();
    var resizeTimerEarly;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimerEarly);
      resizeTimerEarly = setTimeout(function () {
        var nowMobile = isMobile();
        if (nowMobile !== lastIsMobileEarly) window.location.reload();
      }, 250);
    }, { passive: true });
    return;
  }

  /* Build a set of hrefs already on screen so we never duplicate cards. */
  var seenHrefs = new Set();
  function rememberCards(scope) {
    scope.querySelectorAll('a.orw-product-card[href]').forEach(function (a) {
      seenHrefs.add(a.getAttribute('href'));
    });
  }
  rememberCards(grid);
  if (currentProductUrl) seenHrefs.add(currentProductUrl);

  /* Start from page 1: Liquid only renders `products_count` (default 4)
     cards, not a full collection page. Dedupe against `seenHrefs` so
     already-rendered cards are not duplicated. */
  var page = 1;
  var loading = false;
  var done = false;

  /* Sentinel + loader, inline display:block to defeat Dawn empty-div rule. */
  var sentinel = document.createElement('div');
  sentinel.className = 'orw-more-in-collection__infinite-sentinel';
  sentinel.style.cssText = 'display:block;grid-column:1/-1;height:1px;width:100%;';
  grid.appendChild(sentinel);

  var loader = document.createElement('div');
  loader.className = 'orw-more-in-collection__infinite-loader';
  loader.style.cssText = 'display:none;grid-column:1/-1;text-align:center;padding:16px 0;font-family:"Helvetica Neue",Helvetica,Arial,sans-serif;font-size:14px;color:#7C7C7C;';
  loader.textContent = 'Loading more…';
  grid.appendChild(loader);

  function showLoader() { loader.style.display = 'block'; }
  function hideLoader() { loader.style.display = 'none'; }

  function loadNext() {
    if (loading || done) return;
    loading = true;
    showLoader();

    var url = collectionUrl + (collectionUrl.indexOf('?') === -1 ? '?' : '&') + 'page=' + page;

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        /* Scope to the main product grid on the collection page. The page
           also renders "You might also like" / "Explore collection" cards
           in other sections; those would pollute results and break dedup
           if matched broadly. If the collection's template has the
           product-list section disabled (e.g. popular-watches uses the
           default collection.json), there is no [data-orw-product-grid]
           and we stop -- do NOT scrape doc.body, since other sections on
           that page render product cards with the --light variant (white
           text) intended for dark gradient backgrounds, which would
           render invisible on the white more-in-collection background. */
        var collectionGrid = doc.querySelector('[data-orw-product-grid]');
        if (!collectionGrid) {
          done = true;
          return;
        }
        var cards = collectionGrid.querySelectorAll('a.orw-product-card[href]');
        var appended = 0;
        cards.forEach(function (card) {
          var href = card.getAttribute('href');
          if (!href || seenHrefs.has(href)) return;
          seenHrefs.add(href);
          /* Defensive: strip --light if a future caller ever returns it
             so the text renders dark on this white background. */
          card.classList.remove('orw-product-card--light');
          grid.insertBefore(card, sentinel);
          appended += 1;
        });

        var nextLink = doc.querySelector('.orw-product-list__page-btn--next');
        if (!nextLink) {
          done = true;
        } else {
          page += 1;
        }

        /* Safety net: if the page returned no new cards we'd loop forever
           on the same intersection. Stop instead. */
        if (appended === 0) done = true;
      })
      .catch(function () { /* swallow; user can scroll-retry */ })
      .finally(function () {
        loading = false;
        hideLoader();
        if (done && sentinel.parentNode) {
          sentinel.parentNode.removeChild(sentinel);
          if (loader.parentNode) loader.parentNode.removeChild(loader);
          if (observer) { observer.disconnect(); observer = null; }
          return;
        }
        /* IntersectionObserver only fires on transitions. With rootMargin
           400px the sentinel often stays inside the band after a fetch
           (the newly appended cards push it down but not past the band),
           so the observer never re-fires. If sentinel is still within
           viewport + rootMargin after this fetch, call loadNext again
           directly on the next tick so subsequent pages keep flowing.
           Otherwise the next scroll will re-trigger the observer normally. */
        setTimeout(function () {
          if (loading || done) return;
          var rect = sentinel.getBoundingClientRect();
          var vh = window.innerHeight || document.documentElement.clientHeight;
          if (rect.top < vh + 400 && rect.bottom > -400) loadNext();
        }, 50);
      });
  }

  var observer;
  function startObserving() {
    if (!isMobile()) return;
    if (observer) return;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) loadNext();
      });
    }, { rootMargin: '400px 0px' });
    observer.observe(sentinel);
  }

  if (isMobile()) startObserving();

  /* Reload on threshold crossing so the grid mode (mobile <-> desktop)
     returns to its proper layout without a stale infinite-scroll state. */
  var lastIsMobile = isMobile();
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var nowMobile = isMobile();
      if (nowMobile !== lastIsMobile) {
        window.location.reload();
      }
    }, 250);
  }, { passive: true });
})();
